import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { selectedMicrophoneDeviceIdAtom } from '@/components/atoms/AudioDeviceAtom';
import CancelButton from '@/components/atoms/CancelButton';
import AudioLinearProgress from '@/components/organisms/audioTest/AudioLinearProgress';
import useAudioInputTester from '@/components/organisms/audioTest/useAudioInputTester';

export type AudioInputTestDialogProps = {
  open: boolean;
  onClose: (isOk: boolean) => void;
  deviceId?: string;
};

export type AudioInputDevice = {
  deviceId: string;
  label: string;
};
const AudioInputTestDialog = (props: AudioInputTestDialogProps) => {
  const { t } = useTranslation('chat');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const audioTester = useAudioInputTester();
  const audioStart = audioTester.start;
  const audioStop = audioTester.stop;
  const open = props.open;
  const [isPermissionDenied, setIsPermissionDenied] = useState(false);
  const [microphones, setMicrophones] = useState<AudioInputDevice[]>([]);
  const [selectedMicrophoneDeviceId, setSelectedMicrophoneDeviceId] = useAtom(
    selectedMicrophoneDeviceIdAtom
  );

  useEffect(() => {
    if (open) {
      audioStart(selectedMicrophoneDeviceId);
    } else {
      audioStop();
    }
  }, [audioStart, audioStop, open, selectedMicrophoneDeviceId]);

  useEffect(() => {
    const getMicrophones = async () => {
      try {
        // 一度マイクの許可を取らないと label が空になる可能性がある
        await navigator.mediaDevices.getUserMedia({ audio: true });
        setIsPermissionDenied(false); // 許可されていれば false にリセット
        const devices = await navigator.mediaDevices.enumerateDevices();
        //接続されている音声デバイスを取得
        const audioInputs = devices
          .filter((device) => device.kind === 'audioinput')
          .map((device) => ({
            deviceId: device.deviceId,
            label: device.label || 'Unknown Microphone', // デバイスラベルが取得できない場合を考慮（要多言語対応?）
          }));

        setMicrophones(audioInputs);
        // 選択中のマイクがなくなったら、先頭に自動で切り替える
        if (
          !audioInputs.some((d) => d.deviceId === selectedMicrophoneDeviceId)
        ) {
          if (audioInputs.length > 0) {
            const newDeviceId = audioInputs[0].deviceId;
            setSelectedMicrophoneDeviceId(newDeviceId);
          } else {
            setSelectedMicrophoneDeviceId('');
          }
        }
      } catch (error) {
        console.error('Error enumerating devices:', error);
        setIsPermissionDenied(true);
        setMicrophones([]); // 明示的に空配列にしておく
      }
    };

    if (open) {
      getMicrophones();

      // マイクの抜き差しを監視
      navigator.mediaDevices.addEventListener('devicechange', getMicrophones);

      return () => {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          getMicrophones
        );
      };
    }
  }, [setSelectedMicrophoneDeviceId, open, selectedMicrophoneDeviceId]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const newDeviceId = event.target.value;
    setSelectedMicrophoneDeviceId(newDeviceId); //新しいデバイスIDに切り替え
  };

  return (
    <Dialog open={props.open} PaperProps={{ sx: { minHeight: 280 } }}>
      <DialogTitle>
        {
          // マイクをテストします
          t('deviceTest.label009')
        }
      </DialogTitle>
      <DialogContent sx={{ px: 3 }}>
        <Typography mt={1}>{t('deviceTest.label023')}</Typography>
        <Box mt={6} mb={1}>
          <Typography>
            {
              // 入力レベル
              t('deviceTest.label012')
            }
          </Typography>
        </Box>
        <AudioLinearProgress valueRef={audioTester.volume} />
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'space-between',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0,
          alignItems: isMobile ? 'stretch' : 'center',
          px: 3,
          py: 2,
        }}
      >
        {isPermissionDenied || microphones.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            {
              //マイク名が取得できません。
              t('deviceTest.label024')
            }
          </Typography>
        ) : (
          <Box sx={{ minWidth: 120 }}>
            <FormControl fullWidth>
              <InputLabel id="microphone-select-label">
                {t('deviceTest.label016')}
              </InputLabel>
              <Select
                labelId="microphone-select-label"
                id="microphone-select"
                value={selectedMicrophoneDeviceId}
                label={t('deviceTest.label016')} //マイク, Microphone
                onChange={handleChange}
              >
                {microphones.map((mic) => (
                  <MenuItem key={mic.deviceId} value={mic.deviceId}>
                    {mic.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
        <Box
          display="flex"
          gap={1}
          justifyContent={isMobile ? 'flex-end' : 'flex-start'}
        >
          <CancelButton onClick={() => props.onClose(false)} sx={{ px: 1 }}>
            {t('deviceTest.label025')}
          </CancelButton>
          <Button
            onClick={() => {
              props.onClose(true);
            }}
          >
            {
              // はい
              t('deviceTest.label026')
            }
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
export default AudioInputTestDialog;

// デバイス選択ができた頃のダイアログを念のためコメントで保持

// export type AudioInputSelectDialogProps = {
//   open: boolean;
//   onClose: (deviceId: string | undefined) => void;
//   deviceId?: string;
// };

// const AudioInputSelectDialog = (props: AudioInputSelectDialogProps) => {
//   const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
//   const audioTester = useAudioInputTester();
//   const [inited, setInited] = useState(false);
//   const [retryCounter, setRetryCounter] = useState(0);
//   const [selectedDeviceId, setSelectedDeviceId] = useState<string>();

//   useEffect(() => {
//     if (props.open === false) {
//       setInited(false);
//       return;
//     }
//     if (inited) return;
//     setInited(true);
//     (async () => {
//       // デバイスをリスト
//       const devices = await navigator.mediaDevices.enumerateDevices();
//       const inputDevices = devices.filter(
//         (x) => x.kind === 'audioinput' && x.deviceId !== ''
//       );
//       setDevices(inputDevices);
//       const deviceId = props.deviceId ?? inputDevices[0]?.deviceId;
//       setSelectedDeviceId(deviceId);

//       // マイクのテストも実行（許可されていない場合は空欄IDでデバイスの作成を試みることで、deniedを検出できる）
//       await audioTester.start(deviceId ?? '');

//       // デバイス一覧が取得できていなければ、許可を求めている最中のため、繰り返し一覧を取得する。
//       if (inputDevices.length === 0) {
//         await sleep(1000); // 1秒ごとに繰り返す
//         setInited(false);
//         setRetryCounter((v) => v + 1); // リトライ
//       }
//     })();
//   }, [props.open, props.deviceId, audioTester, inited, retryCounter]);

//   const onClose = (deviceId: string | undefined) => {
//     audioTester.stop();
//     props.onClose(deviceId);
//   };

//   return (
//     <Dialog open={props.open} onClose={() => onClose(undefined)}>
//       <DialogTitle>マイク入力に使用するデバイスを選択</DialogTitle>
//       <DialogContent>
//         {audioTester.denied ? (
//           <>
//             マイクへのアクセスが拒否されています。マイクへのアクセスを許可するよう設定を変更してください。
//           </>
//         ) : devices.length === 0 ? (
//           <>
//             マイクへのアクセスを取得しています。マイクへのアクセスを許可してください。
//           </>
//         ) : (
//           <>
//             <Select
//               fullWidth
//               value={selectedDeviceId ?? ''}
//               onChange={async (v) => {
//                 setSelectedDeviceId(v.target.value);
//                 await audioTester.start(v.target.value);
//               }}
//             >
//               {devices.map((x) => (
//                 <MenuItem key={x.deviceId} value={x.deviceId}>
//                   {x.label}
//                 </MenuItem>
//               ))}
//             </Select>
//             <Box mt={2} mb={1}>
//               入力レベル
//             </Box>
//             <LinearProgress variant="determinate" value={audioTester.volume} />
//           </>
//         )}
//       </DialogContent>
//       <DialogActions>
//         <Button
//           disabled={selectedDeviceId == null}
//           onClick={() => {
//             onClose(selectedDeviceId);
//           }}
//         >
//           選択
//         </Button>
//         <CancelButton
//           onClick={() => {
//             onClose(undefined);
//           }}
//         />
//       </DialogActions>
//     </Dialog>
//   );
// };
// export default AudioInputSelectDialog;
